/**
 * `/agents/qa/$runId` — one QA run's evidence bundle (RO-3-13).
 *
 * Rewrite of `excellent/app/agents/qa/[runId]/page.tsx` (182 LOC of server JSX).
 * RO-3-02 ported the `/agents/qa` ledger and deferred this half here, noting
 * that its `rowHref` links would 404 until it landed. They no longer do.
 *
 * ## What made this a REWRITE rather than a port
 *
 * The original called `getQaRun()` from `@excellent/core/browser` inline and
 * composed the entire page — five Cards, the failure block, the step table's
 * column definitions — in the async Server Component body. There was no client
 * component to hand a prop to. The endpoint already existed and was already
 * mounted (`qa.runGet`, `server/routes/agent-qa.ts:49`), so this rewrite needed
 * ZERO server work: it is the pure render half, which is why it is the cheapest
 * of RO-3-13's rewrites and the right one to establish the shape with.
 *
 * ## Decisions
 *
 * **The artifact `<img>`/links keep pointing at `/api/qa/runs/...`.** Those are
 * raw artifact BYTES (a PNG, a JSON blob), not JSON payloads, so they are not
 * contract-shaped and correctly stay plain URLs — `apiCall` exists to carry
 * `{ok,data}` envelopes and would be the wrong seam. The paths are unchanged
 * from the original, and `qaArtifactGet` in the contract documents them.
 *
 * **The mode shell no longer owns the h1.** The original carried a comment that
 * the shell owned the h1 ("Browser QA") so the run's identity had to live in the
 * description. In the new stack this route renders its own `PageHeader`, so the
 * run's label is the title where it belongs, and the description carries the
 * vitals. That is a deliberate improvement the port makes possible, not an
 * accidental drift — flagged here because a reviewer diffing the two will see
 * the header text move.
 *
 * **`pad-ignore` did NOT come along.** The original needed one because the
 * gate's flat lookahead crossed from a `CardContent` into a sibling Card's
 * `DataTable`. This file's structure puts the table's Card at the same nesting
 * depth without the intervening `space-y-3` content block, so no suppression is
 * needed. A rewritten file gets an allowance of 0 and copying the annotation
 * would have been carrying a suppression for a violation that no longer exists.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  formatDateTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { qa, type QaStepRecordDTO } from "@excellent/ui/api/contracts/qa";
import { rootRoute } from "./__root";

const stepColumns: DataTableColumn<QaStepRecordDTO>[] = [
  { key: "step", header: "#", render: (r) => <span className="tabular-nums">{r.step}</span> },
  {
    key: "ok",
    header: "Result",
    render: (r) => (
      <IntentBadge intent={r.ok ? "success" : "danger"}>{r.ok ? "Pass" : "Fail"}</IntentBadge>
    ),
  },
  { key: "op", header: "Op", render: (r) => <code className="type-subtext">{r.op}</code> },
  {
    key: "ms",
    header: "Time",
    render: (r) => <span className="tabular-nums type-subtext">{Math.round(r.ms)}ms</span>,
  },
  {
    key: "url",
    header: "URL",
    render: (r) => (
      <span className="type-subtext text-muted-foreground line-clamp-1">{r.url ?? "—"}</span>
    ),
  },
  {
    key: "reason",
    header: "Detail",
    render: (r) => (
      <span className="type-subtext text-muted-foreground line-clamp-1" title={r.reason}>
        {r.reason ?? "—"}
      </span>
    ),
  },
];

function QaRunPage() {
  const { runId } = agentsQaRunRoute.useParams();
  const { data, error, isLoading } = useApi(qa.runGet, { params: { runId } });

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-muted-foreground">Loading run…</p>
      </div>
    );
  }

  // The original called `notFound()`. The endpoint throws `run-not-found`, so an
  // error here is the same condition — rendered with the back-link intact rather
  // than as a bare crash.
  if (error || !data?.run) {
    return (
      <div className="px-6 py-8 space-y-4">
        <PageHeader
          title="Run not found"
          description="No QA run is archived under that id. Runs without a label are not archived."
        />
        <p className="type-subtext text-muted-foreground">
          <Link href="/agents/qa" className="underline">
            All QA runs
          </Link>
        </p>
      </div>
    );
  }

  const run = data.run;
  const failed = run.failedAt !== null ? run.results[run.failedAt] : undefined;
  const consoleErrors = run.console.filter((c) => c.type === "error");

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title={run.label}
        description={`${run.ok ? "Passed" : "Failed"} · ${run.steps} steps · ${formatDateTime(
          new Date(run.startedAt).toISOString(),
        )}`}
      />

      <p className="type-subtext text-muted-foreground">
        <Link href="/agents/qa" className="underline">
          All QA runs
        </Link>
      </p>

      {!run.ok && failed ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Failed at step {run.failedAt} · <code className="type-subtext">{failed.op}</code>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="type-body text-foreground">{run.reason}</p>
            <p className="type-subtext text-muted-foreground">{failed.url}</p>
            {run.hasScreenshot ? (
              // Artifact BYTES, not a contract payload — a plain URL is correct
              // here (see this file's header). Same path the original used.
              <img
                src={`/api/qa/runs/${encodeURIComponent(run.runId)}/artifact/failure.png`}
                alt={`Page at the moment step ${run.failedAt} failed`}
                className="max-w-full rounded-md border"
              />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        title="Steps"
        rows={run.results}
        columns={stepColumns}
        rowKey={(r) => String(r.step)}
      />

      {consoleErrors.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Console errors ({consoleErrors.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {consoleErrors.slice(0, 30).map((c, i) => (
                <li key={i} className="type-subtext text-muted-foreground break-words">
                  {c.text}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {run.networkFailures.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Network failures ({run.networkFailures.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {run.networkFailures.slice(0, 30).map((n, i) => (
                <li key={i} className="type-subtext text-muted-foreground break-words">
                  {n.errorText} · {n.url}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Artifacts</CardTitle>
        </CardHeader>
        <CardContent>
          {run.artifacts.length === 0 ? (
            <p className="type-body text-muted-foreground">No artifacts captured for this run.</p>
          ) : (
            <ul className="space-y-1">
              {run.artifacts.map((a) => (
                <li key={a} className="type-subtext">
                  {/* Artifact bytes — an external-shaped URL, deliberately not a
                      typed router Link (it leaves the SPA). */}
                  <a
                    href={`/api/qa/runs/${encodeURIComponent(run.runId)}/artifact/${encodeURIComponent(a)}`}
                    className="text-muted-foreground underline"
                  >
                    {a}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const agentsQaRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/qa/$runId",
  component: QaRunPage,
});
